import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { PhoneInput } from "@/components/Inputs/PhoneInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useTRPC, type RouterOutputs } from "@/trpc";
import {
  ProviderContactFields,
  ProviderDetailFields,
} from "@/routes/_org/org/bu/$organizationBusinessUnitId/providers/invitations/_components/invite-provider-fields";
import {
  EMPTY_PROVIDER_FORM_VALUES,
  providerFormSchema,
  toSubmittedPhone,
  type ProviderFormValues,
} from "@/routes/_org/org/bu/$organizationBusinessUnitId/providers/invitations/_components/provider-form-schema";
import type { WorkspaceProvider } from "./workspace-types";

export type WorkspaceRosterEditorMode = "all" | "personal" | "phone";
const EDITOR_TITLES: Record<WorkspaceRosterEditorMode, string> = {
  all: "Edit provider details",
  personal: "Edit personal details",
  phone: "Edit contact phone",
};

export function WorkspaceRosterEditor({
  provider,
  organizationBusinessUnitId,
}: {
  provider: WorkspaceProvider;
  organizationBusinessUnitId: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Edit provider details
      </Button>
      {open && (
        <WorkspaceRosterEditDialog
          provider={provider}
          organizationBusinessUnitId={organizationBusinessUnitId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function WorkspaceRosterEditDialog({
  provider,
  organizationBusinessUnitId,
  onClose,
  mode = "all",
}: {
  provider: WorkspaceProvider;
  organizationBusinessUnitId: string;
  onClose: () => void;
  mode?: WorkspaceRosterEditorMode;
}) {
  const trpc = useTRPC();
  const snapshot = useQuery(
    trpc.providerWorkspace.rosterDetails.queryOptions({
      organizationBusinessUnitId,
      affiliationId: provider.affiliationId,
      providerUserId: provider.providerUserId,
    }),
  );
  const types = useQuery(
    trpc.healthcareProfessionalTypes.getAll.queryOptions(),
  );
  if (snapshot.data && types.data && !snapshot.isError) {
    return (
      <RosterEditorForm
        provider={provider}
        organizationBusinessUnitId={organizationBusinessUnitId}
        snapshot={snapshot.data}
        professionalTypes={types.data}
        onClose={onClose}
        mode={mode}
      />
    );
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{EDITOR_TITLES[mode]}</DialogTitle>
          <DialogDescription>
            Load the complete roster record before editing.
          </DialogDescription>
        </DialogHeader>
        {snapshot.data === null ? (
          <p role="alert">
            This provider can no longer be edited as an unlinked roster record.
            Close this form and reload the provider.
          </p>
        ) : snapshot.isError || types.isError ? (
          <div role="alert">
            <p>{snapshot.error?.message ?? types.error?.message}</p>
            <Button
              variant="outline"
              onClick={() => {
                void snapshot.refetch();
                void types.refetch();
              }}
            >
              Try again
            </Button>
          </div>
        ) : (
          <p role="status">Loading provider details…</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RosterEditorForm({
  provider,
  organizationBusinessUnitId,
  snapshot,
  professionalTypes,
  onClose,
  mode,
}: {
  provider: WorkspaceProvider;
  organizationBusinessUnitId: string;
  snapshot: NonNullable<RouterOutputs["providerWorkspace"]["rosterDetails"]>;
  professionalTypes: { id: string; name: string }[];
  onClose: () => void;
  mode: WorkspaceRosterEditorMode;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  // A background refetch must not pair an old form draft with a newer version
  // token. Keep values and their expected version together for this edit.
  const [editingSnapshot] = useState(snapshot);
  const invitation = editingSnapshot.invitation;
  const form = useForm<ProviderFormValues>({
    resolver: zodResolver(providerFormSchema),
    defaultValues: {
      ...EMPTY_PROVIDER_FORM_VALUES,
      firstName: invitation.firstName ?? "",
      lastName: invitation.lastName ?? "",
      email: invitation.email ?? "",
      phone: invitation.phone ?? "",
      npi: invitation.npi ?? "",
      specialtyCodes: invitation.specialtyCodes ?? [],
      healthcareProfessionalType:
        professionalTypes.find(
          (type) => type.id === invitation.healthcareProfessionalTypeId,
        ) ??
        (invitation.healthcareProfessionalTypeId
          ? {
              id: invitation.healthcareProfessionalTypeId,
              name: provider.role || "Current role",
            }
          : undefined),
    },
  });
  const email = form.watch("email");
  const retargeting =
    invitation.inviteSentAt !== null &&
    email.trim().toLowerCase() !== (invitation.email ?? "").toLowerCase();
  const save = useMutation(
    trpc.providerWorkspace.updateRosterDetails.mutationOptions({
      onSuccess: async (_, variables) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.providerWorkspace.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.credentials.getForBu.queryKey({
              organizationBusinessUnitId: variables.organizationBusinessUnitId,
              providerUserId: variables.providerUserId,
            }),
          }),
        ]);
        toast.success(
          retargeting
            ? "Provider updated. Send a new invitation to the updated email."
            : "Provider updated.",
        );
        onClose();
      },
      onError: (error) => toast.error(error.message),
    }),
  );
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{EDITOR_TITLES[mode]}</DialogTitle>
          <DialogDescription>
            Update the details your organization supplied. Clearing a field
            removes it from the roster.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit((values) =>
              save.mutate({
                organizationBusinessUnitId,
                affiliationId: provider.affiliationId,
                providerUserId: provider.providerUserId,
                invitationId: invitation.id,
                expectedVersion: editingSnapshot.expectedVersion,
                firstName: values.firstName || null,
                lastName: values.lastName || null,
                email: values.email || null,
                phone: toSubmittedPhone(values.phone),
                npi: values.npi || null,
                healthcareProfessionalTypeId:
                  values.healthcareProfessionalType?.id ?? null,
                specialtyCodes: values.specialtyCodes ?? [],
              }),
            )}
          >
            {mode === "all" && <ProviderContactFields form={form} />}
            {mode === "phone" && (
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone (optional)</FormLabel>
                    <FormControl>
                      <PhoneInput
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        placeholder="(555) 555-5555"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {mode === "all" && retargeting && (
              <p className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Changing this email cancels the invitation sent to{" "}
                {invitation.email}. Send a new invitation after saving.
              </p>
            )}
            {mode !== "phone" && <ProviderDetailFields form={form} />}
            {form.formState.errors.root?.message && (
              <p role="alert">{form.formState.errors.root.message}</p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={save.isPending}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
